import type { Color, IconName } from "@salestrak/ui";

/**
 * Gallery sections — the dashboard domain's answer to "what does this widget
 * MEASURE." One axis, not three: every section names a thing in the workspace a
 * number can be counted over, so a reader scanning the nav is choosing a subject
 * rather than a chart shape or an audience. `Custom` and `Embeds & apps` are the
 * two deliberate exceptions — they hold the widgets that measure whatever the
 * user points them at, and the ones that measure nothing at all.
 *
 * A preset declares its home here through its catalog `section` and lands in
 * EXACTLY ONE. The gallery groups by that field and nothing else, so shipping a
 * widget files it automatically — there is no second list in the dialog to keep
 * in step, and no way for a preset to appear twice or go missing.
 *
 * The domain owns this vocabulary; the domain-free `widget-kit` only carries the
 * opaque `section` string a definition declares (`WidgetCatalogEntry.section`).
 * Each entry writes it with `satisfies WidgetSection` so a typo is a compile
 * error — never magic strings. Sibling of `widget-tags.ts`, which stays the
 * CROSS-CUTTING vocabulary: a widget sits in one section but carries many tags,
 * and tags feed search + chips.
 */
export const WIDGET_SECTIONS = {
  /** Curated, scope-aware picks. The one section no preset declares — its
   *  members are named by `FEATURED_PRESETS` below. */
  featured: { label: "Featured", icon: "star", color: "orange" },
  /** Blank, fully-configurable starting points: pick the shape, then point it
   *  at whatever you want. Every entry here stays untitled (`autoTitle: false`)
   *  because the card names itself from what the user configures. */
  custom: { label: "Custom", icon: "sliders-horizontal", color: "light-blue" },
  projects: { label: "Projects", icon: "folder-kanban", color: "blue" },
  stages: { label: "Stages", icon: "layers", color: "violet" },
  members: { label: "Members", icon: "user", color: "green" },
  /** `heart-pulse` is THE follow-up family glyph (updates, age chips, the
   *  posting act) — an outcome is what an Update records, so it wears the
   *  family mark rather than a second one invented here. */
  outcomes: { label: "Outcomes", icon: "heart-pulse", color: "yellow" },
  activity: { label: "Activity", icon: "activity", color: "pink" },
  workflows: { label: "Workflows", icon: "workflow", color: "teal" },
  embeds: { label: "Embeds & apps", icon: "code-xml", color: "grey" },
} as const satisfies Record<string, { label: string; icon: IconName; color: Color }>;

export type WidgetSection = keyof typeof WIDGET_SECTIONS;

/**
 * Nav order, top to bottom. Featured leads; Custom follows it because "build my
 * own" is the second thing a returning user reaches for. Then the subjects in
 * descending breadth, and the two content sections last.
 */
export const WIDGET_SECTION_ORDER = [
  "featured",
  "custom",
  "projects",
  "stages",
  "members",
  "outcomes",
  "activity",
  "workflows",
  "embeds",
] as const satisfies readonly WidgetSection[];

/** Narrow a preset's open `section` string to a known section. A preset naming
 *  no live section is dropped from the gallery rather than inventing a row. */
export function isWidgetSection(value: string): value is WidgetSection {
  return value in WIDGET_SECTIONS;
}

/**
 * The dashboard entity a gallery was opened over — `DashboardScope`'s
 * `entity?.type` verbatim, which that context keeps as an OPEN string on
 * purpose. Narrowing it here would restate a type this module does not own, so
 * the resolver below matches the two kinds it has picks for and every other
 * value (including `undefined`, a saved view pinning no entity) takes the
 * default.
 */
export type GalleryScopeKind = string | undefined;

/**
 * Featured is CURATION, so it is the one hand-picked list left, and its rule is
 * "the widgets Custom cannot make". Every Custom tile is a blank shape (a line,
 * a bar, a pie, a list, a number) the user fills in; Featured leads with the
 * purpose-built reads — conversion, funnel, outcomes, completions,
 * activity — that no shape produces, so a new dashboard opens on real value and
 * Custom stays the answer to "I want something different". A shape preset with
 * a default filled in is NOT featured: it would only restate Custom.
 *
 * It reads the host scope so a workflow board leads with stage flow and a team
 * board with people, instead of both showing the same cards.
 *
 * These keys duplicate their home section on purpose: Featured is a shortcut
 * layer over the taxonomy, not a category in it. Everywhere else, one preset =
 * one section.
 */
const FEATURED_PRESETS: Record<"team" | "workflow" | "default", readonly string[]> = {
  team: [
    "outcome-performance.summary",
    "workflow-roster.default",
    "projects-closed.default",
    "stage-conversion.rate",
  ],
  workflow: [
    "stage-conversion.default",
    "stage-funnel.stage",
    "stage-age.stage",
    "projects-closed.default",
  ],
  default: [
    "stage-conversion.default",
    "stage-funnel.stage",
    "outcome-performance.summary",
    "projects-closed.default",
    "activity-list.default",
  ],
};

/** The Featured keys for a host scope, in display order. A member scope (My
 *  Work) never reaches the gallery — it is managed — so it takes the default. */
export function featuredPresetKeys(scope: GalleryScopeKind): readonly string[] {
  if (scope === "team") return FEATURED_PRESETS.team;
  if (scope === "workflow" || scope === "workflows") return FEATURED_PRESETS.workflow;
  return FEATURED_PRESETS.default;
}

/**
 * Display order INSIDE a section, as preset keys. This is a PREFERENCE, not an
 * allowlist — the distinction is the whole point. A key named here sorts to the
 * front in this order; every other preset in the section follows in registry
 * order, so shipping a widget can never leave it invisible the way the retired
 * `presetKeys` allowlist did. An unknown key is ignored, so a renamed or
 * unshipped preset costs nothing.
 *
 * Only sections whose reading order carries meaning declare one. `custom` does:
 * its tiles read as a shape menu (line → bar → pie → donut → battery → treemap →
 * number → note), which is a deliberate progression, not alphabetical.
 */
const SECTION_PRESET_ORDER: Partial<Record<WidgetSection, readonly string[]>> = {
  custom: [
    "series-line.custom",
    "dimension-bar.custom",
    "type-mix.pie",
    "type-mix.donut",
    "dimension-battery.custom",
    "portfolio-map.treemap",
    "metric.custom",
    "note.default",
    "section-header.default",
  ],
};

/**
 * Sort a section's presets: the declared order first, everything else after it in
 * the order the registry handed them over. Stable, and total — nothing is dropped.
 */
export function orderSectionPresets<T extends { key: string }>(
  section: WidgetSection,
  presets: T[],
): T[] {
  const order = SECTION_PRESET_ORDER[section];
  if (!order) return presets;
  const rank = new Map(order.map((key, index) => [key, index]));
  return [...presets].sort(
    (a, b) =>
      (rank.get(a.key) ?? Number.MAX_SAFE_INTEGER) -
      (rank.get(b.key) ?? Number.MAX_SAFE_INTEGER),
  );
}
